import React, { useEffect, useState } from "react";
import { MenuItem, Switch, TextField } from "@mui/material";
import { message } from "antd";
import styles from "./AddCategory.module.css";
import CategoryTable from "../components/CategoryTable";
import APIURL from "../api/apiClient";

const initialFormState = {
  name: "",
  thumbnail: null,
  thumbnailPreview: "",
  status: 1,
  featured: "",
};

const createInitialFormState = () => ({ ...initialFormState });

const normalizeApiResponse = (response) => {
  if (typeof response !== "string") {
    return response;
  }

  try {
    return JSON.parse(response.trim() || "{}");
  } catch {
    return { success: false, message: response };
  }
};

function AddCategory() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [formData, setFormData] = useState(createInitialFormState);
  const [errorMessage, setErrorMessage] = useState("");

  const validate = () => {
    if (!formData.name.trim()) {
      setErrorMessage("Name is required.");
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const handleChange = (event) => {
    const { name, value, type, checked, files } = event.target;

    if (type === "checkbox") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked ? 1 : 0,
      }));
      return;
    }

    if (type === "file" && name === "thumbnail") {
      const thumbnailFile = files?.[0] || null;

      setFormData((prevData) => {
        if (prevData.thumbnailPreview) {
          URL.revokeObjectURL(prevData.thumbnailPreview);
        }

        return {
          ...prevData,
          thumbnail: thumbnailFile,
          thumbnailPreview: thumbnailFile ? URL.createObjectURL(thumbnailFile) : "",
        };
      });
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    return () => {
      if (formData.thumbnailPreview) {
        URL.revokeObjectURL(formData.thumbnailPreview);
      }
    };
  }, [formData.thumbnailPreview]);

  const resetForm = () => {
    setFormData((prevData) => {
      if (prevData.thumbnailPreview) {
        URL.revokeObjectURL(prevData.thumbnailPreview);
      }

      return createInitialFormState();
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const formPayload = new FormData();
    formPayload.append("name", formData.name.trim());
    formPayload.append("status", formData.status);
    formPayload.append("featured", formData.featured);

    if (formData.thumbnail instanceof File) {
      formPayload.append("thumbnail", formData.thumbnail);
    }

    try {
      const response = await APIURL.post("/insertcategory.php", formPayload, true);
      const normalizedResponse = normalizeApiResponse(response);

      if (normalizedResponse?.success) {
        message.success(normalizedResponse.message || "Category added successfully.");
        setErrorMessage("");
        resetForm();
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setErrorMessage(normalizedResponse?.error || normalizedResponse?.message || "Submission failed.");
      }
    } catch (error) {
      console.error("Error submitting category:", error);
      setErrorMessage(error?.message || "Submission failed. Please try again.");
    }
  };

  return (
    <div className={styles.categoryContainer}>
      <div className="container mt-4">
        <div className="row d-flex align-items-start">
          <div className="col-md-5">
            <h2>Create Category</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <TextField
                  type="text"
                  name="name"
                  size="small"
                  label="Category Name"
                  className={styles.formControl}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {errorMessage ? <div className={styles.errorMessage}>{errorMessage}</div> : null}

              <div className={styles.thumbnailSection}>
                <label htmlFor="thumbnail" className={styles.thumbnailLabel}>
                  {formData.thumbnailPreview ? (
                    <>
                      <img
                        src={formData.thumbnailPreview}
                        alt="Category preview"
                        className={styles.thumbnailPreview}
                      />
                      <span>Change Image</span>
                    </>
                  ) : (
                    <>
                      <div className={styles.uploadIcon}>+</div>
                      <span>Add Category Image</span>
                    </>
                  )}
                  <input
                    type="file"
                    name="thumbnail"
                    id="thumbnail"
                    onChange={handleChange}
                    accept="image/png, image/jpeg, image/webp"
                    className={styles.fileInput}
                  />
                </label>
              </div>

              <div className={styles.switchContainer}>
                <span className={styles.switchLabel}>Status</span>
                <Switch
                  checked={formData.status === 1}
                  onChange={(event) =>
                    handleChange({
                      target: {
                        name: "status",
                        type: "checkbox",
                        checked: event.target.checked,
                      },
                    })
                  }
                  name="status"
                />
              </div>

              <div className={styles.formGroup}>
                <TextField
                  select
                  name="featured"
                  size="small"
                  label="Featured"
                  className={styles.formControl}
                  value={formData.featured}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="" disabled>
                    Select Featured Option
                  </MenuItem>
                  <MenuItem value="1">Computer</MenuItem>
                  <MenuItem value="0">Laptop</MenuItem>
                </TextField>
              </div>

              <button type="submit" className={styles.submitButton}>
                Submit
              </button>
            </form>
          </div>

          <div className="col-md-7">
            <CategoryTable refreshTrigger={refreshTrigger} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddCategory;
